import * as React from "react";
import {ChevronLeft, ChevronRight} from "lucide-react";
import {DayPicker} from "react-day-picker";
import type {DateRange} from "react-day-picker";
import {ja} from "react-day-picker/locale";

import {cn} from "../lib/utils";
import {buttonVariants} from "./button";

export type {DateRange};
export type CalendarProps = React.ComponentProps<typeof DayPicker>;

// --- Module-level constants (computed once, stable references across renders) ---

const navButtonClass = cn(buttonVariants({variant: "outline"}), "bg-muted/30 !p-0 !h-[2.25rem] !w-[2.25rem] text-foreground opacity-80 hover:opacity-100 hover:bg-accent transition-all duration-150");
const dayButtonClass = cn(buttonVariants({variant: "ghost"}), "!p-0 !font-normal transition-colors duration-150");

const CHEVRON_ICON_STYLE = {width: 18, height: 18, minWidth: 18};
const DAY_CELL_SIZE = "2.25rem";

const DEFAULT_STYLES = {
  day_button: {width: DAY_CELL_SIZE, height: DAY_CELL_SIZE},
  weekday: {width: DAY_CELL_SIZE},
} as const;

function Chevron({orientation}: {orientation?: string}) {
  const Icon = orientation === "left" ? ChevronLeft : ChevronRight;
  return <Icon style={CHEVRON_ICON_STYLE} />;
}

const DEFAULT_COMPONENTS = {Chevron} as const;

// --- Calendar component ---

function Calendar({className, classNames, showOutsideDays = true, locale = ja, ...props}: CalendarProps) {
  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      locale={locale}
      navLayout="around"
      className={cn("p-4 w-fit", className)}
      classNames={{
        // Layout — navLayout="around" places prev/caption/next as siblings inside month
        months: "flex flex-col sm:flex-row sm:items-start gap-xs sm:gap-6",
        month: "grid grid-cols-[auto_1fr_auto] items-center gap-y-[1rem]",
        month_caption: "text-center col-start-2",
        caption_label: "text-md font-medium",
        button_previous: cn(navButtonClass, "col-start-1"),
        button_next: cn(navButtonClass, "col-start-3"),

        // Table structure (v9 uses native <table> — do NOT add flex)
        month_grid: "w-full border-collapse col-span-full",
        weekday: "text-muted-foreground pb-2 font-medium text-[0.75rem] tracking-wide",
        weekdays: "border-b border-border",

        // Day cell (<td>) — state classes (selected, today, etc.) are applied here
        day: cn(
          "relative p-0 text-center text-sm [font-variant-numeric:tabular-nums] focus-within:relative focus-within:z-20",
          props.mode === "range"
            ? "data-[selected]:bg-accent first:data-[selected]:rounded-l-md last:data-[selected]:rounded-r-md"
            : "data-[selected]:rounded-md",
        ),

        // Day button (<button>) inside the cell
        day_button: dayButtonClass,

        // Selection states (applied to <td>, use [&>button] to style child button)
        // In range mode, selected is applied to ALL days in the range (start+middle+end).
        // Keep it minimal so range_start/middle/end can control visuals without specificity battles.
        selected:
          props.mode === "range"
            ? ""
            : "[&>button]:bg-primary [&>button]:text-primary-foreground [&>button]:hover:bg-primary [&>button]:hover:text-primary-foreground [&>button]:focus:bg-primary [&>button]:focus:text-primary-foreground rounded-md",
        today: "[&>button]:!font-bold [&>button]:ring-1 [&>button]:ring-inset [&>button]:ring-primary/40 [&>button]:rounded-md",
        outside: "[&>button]:text-muted-foreground [&>button]:opacity-40 data-[selected]:[&>button]:opacity-100",
        disabled: "[&>button]:text-muted-foreground [&>button]:opacity-50",
        hidden: "invisible",

        // Range selection — each handles its own visual styling independently
        range_start: "rounded-l-md [&>button]:bg-primary [&>button]:text-primary-foreground [&>button]:hover:bg-primary [&>button]:focus:bg-primary [&>button]:focus:text-primary-foreground",
        range_end: "rounded-r-md [&>button]:bg-primary [&>button]:text-primary-foreground [&>button]:hover:bg-primary [&>button]:focus:bg-primary [&>button]:focus:text-primary-foreground",
        range_middle: "[&>button]:bg-transparent [&>button]:text-accent-foreground [&>button]:hover:bg-accent/80",

        ...classNames,
      }}
      styles={DEFAULT_STYLES}
      components={DEFAULT_COMPONENTS}
      {...props}
    />
  );
}

export {Calendar};
